import {
  Badge,
  Box,
  Button,
  ButtonProps,
  Checkbox,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react';
import type { LucideIcon } from 'lucide-react';

import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={1}>
      {children}
    </Text>
  );
}

type PickerRequirement = { code: string; description: string };

export type RequirementPickerGroup<R extends PickerRequirement> = {
  key: string;
  label: React.ReactNode;
  rows: readonly R[];
  greyed: boolean;
  linked: boolean;
};

function RequirementOption({
  code,
  description,
  colour,
  greyed,
  linked,
  holderTag,
  optionTestId,
  onToggle,
}: {
  code: string;
  description: string;
  colour: string;
  greyed: boolean;
  linked: boolean;
  holderTag: string | undefined;
  optionTestId: string;
  onToggle: () => void;
}) {
  return (
    <Checkbox
      isChecked={linked}
      w="full"
      px={2}
      py={1.5}
      borderRadius="md"
      _hover={{ bg: 'bg.muted' }}
      data-testid={optionTestId}
      onChange={onToggle}
    >
      <HStack spacing={2} align="center">
        <Badge colorScheme={greyed ? 'gray' : colour} fontFamily="mono" fontSize="xs">
          {code}
        </Badge>

        {holderTag != null ? (
          <Badge colorScheme="gray" fontSize="xs" flexShrink={0}>
            {holderTag}
          </Badge>
        ) : null}

        <Text fontSize="xs" noOfLines={2} color={greyed ? 'fg.subtle' : undefined} flex={1}>
          {description}
        </Text>
      </HStack>
    </Checkbox>
  );
}

export function RequirementPicker<R extends PickerRequirement>({
  groups,
  triggerLabel,
  triggerIcon: TriggerIcon,
  triggerProps,
  optionTestId,
  idOf,
  colourOf,
  holderTagOf,
  onToggle,
}: {
  groups: readonly RequirementPickerGroup<R>[];
  triggerLabel: React.ReactNode;
  triggerIcon: LucideIcon;
  triggerProps?: ButtonProps & { 'data-testid'?: string };
  optionTestId: string;
  idOf: (requirement: R) => string;
  colourOf: (requirement: R) => string;
  holderTagOf?: (requirement: R) => string | undefined;
  onToggle: (requirement: R, linked: boolean) => void;
}) {
  const visibleGroups = groups.filter(({ rows }) => rows.length > 0);

  return (
    <Popover placement="bottom-start" isLazy>
      <PopoverTrigger>
        <Button
          type="button"
          size="xs"
          variant="outline"
          leftIcon={<TriggerIcon size={iconSize.xs} />}
          {...triggerProps}
        >
          {triggerLabel}
        </Button>
      </PopoverTrigger>

      <PopoverContent w={pxToRem(420)}>
        <PopoverArrow />

        <PopoverBody maxH={pxToRem(360)} overflowY="auto">
          <VStack align="stretch" spacing={2}>
            {visibleGroups.map(({ key, label, rows, greyed, linked }, index) => (
              <Box key={key} pt={index === 0 ? 0 : 1}>
                <GroupLabel>{label}</GroupLabel>

                <VStack align="stretch" spacing={0}>
                  {rows.map((requirement) => (
                    <RequirementOption
                      key={idOf(requirement)}
                      code={requirement.code}
                      description={requirement.description}
                      colour={colourOf(requirement)}
                      greyed={greyed}
                      linked={linked}
                      holderTag={holderTagOf?.(requirement)}
                      optionTestId={optionTestId}
                      onToggle={() => onToggle(requirement, !linked)}
                    />
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
